import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from '@/api/client'
import { postWhenCoachReady, coachProgressLabel, type CoachProgress } from '@/api/coach'
import { CoachPanel } from '@/components/CoachPanel'
import type { CoachThread, Role, Task } from '@/lib/types'
import { useAsync } from '@/components/useAsync'
import { ReviewPicker } from './ReviewPicker'
import type { ReviewHistory } from './types'

export function DraftReview({
  task,
  role,
  refresh,
  dirty = false,
  onDirty,
}: {
  task: Task
  role: Role
  refresh: () => void
  dirty?: boolean
  onDirty?: (dirty: boolean) => void
}) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [progress, setProgress] = useState<CoachProgress | null>(null)
  const [thread, setThread] = useState<CoachThread | null>(null)
  const [threadLoaded, setThreadLoaded] = useState(false)
  const [refreshToken, setRefreshToken] = useState(0)
  const controller = useRef<AbortController>()
  useEffect(() => () => controller.current?.abort(), [])
  const [reason, setReason] = useState('')
  useEffect(() => { onDirty?.(!!reason || busy) }, [reason, busy, onDirty])
  const requestId = useRef(task.coach_review?.review_id ?? crypto.randomUUID())
  const review = task.coach_review
  const [selectedReview, setSelectedReview] = useState('')
  const history = useAsync(
    () => (review ? api.get<ReviewHistory>(`/tasks/${task.task_id}/reviews`) : Promise.resolve(null)),
    [task.task_id, review?.review_id],
  )
  const previous = history.data?.coach_reviews.filter((item) => item.review_id !== review?.review_id) ?? []
  useEffect(() => {
    setSelectedReview('')
    setThreadLoaded(false)
    setThread(null)
    if (review?.review_id && !controller.current) requestId.current = review.review_id
  }, [review?.review_id])
  async function request(fresh = false) {
    if (controller.current) return
    setSelectedReview('')
    if (fresh) requestId.current = crypto.randomUUID()
    const current = new AbortController()
    controller.current = current
    setBusy(true)
    setError('')
    try {
      const result = await postWhenCoachReady<{ thread: CoachThread }>(
        `/tasks/${task.task_id}/coach-reviews`,
        {
          revision: task.revision ?? 0,
          request_id: requestId.current,
        },
        { signal: current.signal, onProgress: setProgress },
      )
      setThread(result.thread)
      setRefreshToken((k) => k + 1)
      refresh()
    } catch (e) {
      if (!current.signal.aborted) {
        setError((e as Error).message)
        refresh()
      }
    } finally {
      controller.current = undefined
      setBusy(false)
      setProgress(null)
    }
  }
  async function waive() {
    setBusy(true)
    setError('')
    try {
      await api.post(`/tasks/${task.task_id}/coach-reviews/waiver`, { reason })
      setReason('')
      refresh()
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  const onThreadChange = useCallback((next: CoachThread | null) => {
    setThread(next)
    setThreadLoaded(true)
  }, [])
  const submitted = !!thread?.message_count
  const failed = thread?.status === 'failed'
  return (
    <section className="box">
      <div className="panel-section-head">
        <div className="draft-review-heading">
          <h2>Draft review</h2>
          {review?.stale && <span className="small muted">Draft changed since this review</span>}
        </div>
        <div className="draft-review-controls">
          {previous.length > 0 && (
            <ReviewPicker previous={previous} value={selectedReview} onChange={setSelectedReview} />
          )}
          {role === 'sme' && review && review.status !== 'pending' && (
            <button className="link small" disabled={busy || dirty} onClick={() => void request(true)}>
              Review again
            </button>
          )}
        </div>
      </div>
      <div hidden={!!selectedReview}>
        {busy ? (
          <p className="coach-status busy" role="status">
            {progress ? coachProgressLabel[progress] : 'Starting review…'}
          </p>
        ) : role === 'sme' &&
          (!review || (review.status === 'pending' && threadLoaded && (!submitted || failed))) ? (
          <button className="primary sm" disabled={dirty} onClick={() => void request(failed)}>
            {failed ? 'Try review again' : review ? 'Resume draft review' : 'Review draft with coach'}
          </button>
        ) : null}
        {review?.review_id && (
          <CoachPanel
            key={review.review_id}
            refreshToken={refreshToken}
            taskId={task.task_id}
            role={role}
            compact
            awaitingReview={review.status === 'pending'}
            onThreadChange={onThreadChange}
            conversationUrl={`/tasks/${task.task_id}/conversations/draft-${review.review_id}`}
          />
        )}
      </div>
      {selectedReview && (
        <CoachPanel
          key={selectedReview}
          taskId={task.task_id}
          role={role}
          compact
          readOnly
          conversationUrl={`/tasks/${task.task_id}/conversations/draft-${selectedReview}`}
        />
      )}
      {history.error && (
        <p className="small muted">
          Previous reviews couldn’t load.{' '}
          <button className="link small" onClick={history.reload}>
            Retry
          </button>
        </p>
      )}
      {role === 'engineer' && review?.status !== 'completed' && (
        <details>
          <summary>Coach unavailable?</summary>
          <label>
            Reason for exception
            <textarea value={reason} onChange={(e) => setReason(e.target.value)} maxLength={1600} />
          </label>
          <button disabled={busy || !reason.trim()} onClick={waive}>
            Allow execution
          </button>
          {reason && <button className="link small" disabled={busy} onClick={() => setReason('')}>Discard reason</button>}
        </details>
      )}
      {error && (
        <p className="note err" role="alert">
          {error}
        </p>
      )}
    </section>
  )
}
